<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import type { PropertyOnList } from '@@/shared/property/api-types';
import type { PropertyOnCard } from '@@/shared/property/types';
import type { IadAppRoute } from '~/application';

const props = withDefaults(
  defineProps<{
    property: PropertyOnList | PropertyOnCard
    variant?: 'full' | 'lite'
    titleTag?: 'p' | 'h2' | 'h3'
  }>(),
  {
    variant: 'full',
    titleTag: 'h2',
  },
);

const { t, locale } = useI18n();
const { surface: formatSurface, amount } = useIadFormat();
const { title: propertyTitle } = usePropertyTitle(toRef(() => props.property));

const propertyUrl = computed<IadAppRoute<`property-propertySlug-rpropertyListingRef`>>(() => ({
  name: `property-propertySlug-rpropertyListingRef`,
  params: {
    propertySlug: props.property.slugs[locale.value] ?? ``,
    propertyListingRef: String(props.property.propertyListingRef),
  },
}));

const isDescriptionDisplayed = ref(false);
function toggleDescription() {
  isDescriptionDisplayed.value = !isDescriptionDisplayed.value;
}

const route = useRoute();
const returnTo = useStorage(`property-${props.property.propertyListingRef}`, route.fullPath);
watch(() => route.fullPath, () => {
  returnTo.value = route.fullPath;
}, { immediate: true });

// NOTE: About markup
// • for SEO purpose <NuxtLink><IadTitle /></NuxtLink>
// • to make whole card clickable <ClientOnly><NuxtLink><ClientOnly>
</script>

<template>
  <article class="rounded-xl overflow-clip relative border border-neutral-70 isolate flex flex-col">
    <IadTag
      v-if="property.isExclusive"
      size="large"
      class="absolute top-xs left-xs z-20"
      :text="t(`property.tags.exclusive`)"
    />
    <PropertyCardSquarePhoto
      v-if="variant === `lite`"
      :property
    />
    <PropertyCardCarrousel
      v-else
      :property
      :property-url
    />
    <div class="p-m flex-1 flex flex-col">
      <div class="flex flex-wrap gap-m justify-between pb-2xs">
        <div>
          <PropertyTags
            :property
            class="mb-2xs"
          />
          <div class="text-secondary">
            <IadTitle
              tag="p"
              :text="amount(property.prices.main)"
              variant="medium"
              class="inline"
            />
            <IadTitle
              v-if="property.transactionType === 'rental'"
              tag="span"
              variant="xx-small"
              class="before:content-['_']"
              :text="$t('property.price.rental.charges')"
            />
            <span
              v-if="property.isContractSigned"
              class="before:content-['_•_'] font-semibold whitespace-nowrap"
              v-text="t(`property.tags.contract_signed`)"
            />
          </div>
        </div>
      </div>
      <div>
        <NuxtLinkLocale
          :to="propertyUrl"
        >
          <IadTitle
            variant="x-small"
            :text="propertyTitle"
            :tag="titleTag"
          />
        </NuxtLinkLocale>
      </div>
      <div
        class="marker:hidden flex justify-between gap-xs relative z-10"
        @click="toggleDescription"
      >
        <ul>
          <li
            v-for="room in property.rooms"
            :key="room.type"
            class="inline before:content-['_•_'] first:before:content-none"
            v-text="t(`property.common.${room.type}_count`, { count: room.value })"
          />
          <li
            v-for="surface in property.surfaces"
            :key="surface.type"
            class="inline before:content-['_•_'] first:before:content-none"
            v-text="t(`property.surfaces.${surface.type}`, { surface: formatSurface(surface.value) })"
          />
        </ul>
        <IadBtn
          v-if="variant === `full`"
          size="small"
          variant="outlined"
          icon
        >
          <Icon
            name="iad-icon:caret-down"
            :class="{ 'rotate-180': isDescriptionDisplayed }"
          />
        </IadBtn>
      </div>
      <template v-if="isDescriptionDisplayed && variant === 'full'">
        <div
          class="line-clamp-4 pt-m"
          v-html="property.description"
        />
        <IadBtn
          :text="t('core.common.see_listing')"
          class="mt-xs w-full"
        />
      </template>
      <PropertyCardAgent
        v-if="'agent' in property"
        :property
        :agent="property.agent"
      />
    </div>
    <ClientOnly>
      <NuxtLinkLocale
        :to="propertyUrl"
        class="absolute inset-0"
      />
    </ClientOnly>
  </article>
</template>
